import React, { useEffect, useState } from 'react';

import {
  Container,
  Card,
  Button,
  Form,
  Modal,
  Alert,
  Spinner,
  Table,
  Badge,
} from 'react-bootstrap';

import api from '../api';


function PurchaseBatches() {
  const [batches, setBatches] = useState([]);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState('');


  const [
    showCreateModal,
    setShowCreateModal,
  ] = useState(false);

  const [
    newBatchName,
    setNewBatchName,
  ] = useState('');

  const [
    newBatchTotal,
    setNewBatchTotal,
  ] = useState('');

  const [
    creating,
    setCreating,
  ] = useState(false);


  const [
    selectedBatch,
    setSelectedBatch,
  ] = useState(null);

  const [
    loadingBatch,
    setLoadingBatch,
  ] = useState(false);


  const [
    showEditModal,
    setShowEditModal,
  ] = useState(false);

  const [
    editBatchName,
    setEditBatchName,
  ] = useState('');

  const [
    editBatchTotal,
    setEditBatchTotal,
  ] = useState('');

  const [
    savingEdit,
    setSavingEdit,
  ] = useState(false);


  const [
    recalculating,
    setRecalculating,
  ] = useState(false);


  const [
    removingSku,
    setRemovingSku,
  ] = useState('');


  useEffect(() => {
    fetchBatches();
  }, []);


  // --------------------------------------------------
  // Load all Purchase Batches
  // --------------------------------------------------

  const fetchBatches = async () => {
    try {
      setLoading(true);

      setError('');

      const response =
        await api.get(
          '/purchase-batches'
        );

      setBatches(
        Array.isArray(
          response.data?.batches
        )
          ? response.data.batches
          : []
      );
    } catch (err) {
      console.error(
        '[PurchaseBatches] Load error:',
        err
      );

      setError(
        err.response?.data?.error ||
          'Failed to load purchase batches.'
      );
    } finally {
      setLoading(false);
    }
  };


  // --------------------------------------------------
  // Create Batch
  // --------------------------------------------------

  const createBatch = async () => {
    const name =
      newBatchName.trim();

    const totalSpent =
      Number(
        newBatchTotal
      );


    if (!name) {
      alert(
        'Enter a batch name.'
      );

      return;
    }


    if (
      !Number.isFinite(
        totalSpent
      ) ||
      totalSpent < 0
    ) {
      alert(
        'Enter a valid total spent.'
      );

      return;
    }


    try {
      setCreating(true);

      await api.post(
        '/purchase-batches',
        {
          name,

          total_spent:
            totalSpent,
        }
      );


      setNewBatchName('');

      setNewBatchTotal('');

      setShowCreateModal(
        false
      );


      await fetchBatches();
    } catch (err) {
      console.error(
        '[PurchaseBatches] Create error:',
        err
      );


      alert(
        err.response?.data?.error ||
          'Failed to create purchase batch.'
      );
    } finally {
      setCreating(false);
    }
  };


  // --------------------------------------------------
  // Open Batch
  // --------------------------------------------------

  const openBatch = async (
    batchId
  ) => {
    try {
      setLoadingBatch(true);

      setError('');


      const response =
        await api.get(
          `/purchase-batches/${batchId}`
        );


      setSelectedBatch(
        response.data?.batch ||
          null
      );
    } catch (err) {
      console.error(
        '[PurchaseBatches] Load batch error:',
        err
      );


      setError(
        err.response?.data?.error ||
          'Failed to load purchase batch.'
      );
    } finally {
      setLoadingBatch(false);
    }
  };


  // --------------------------------------------------
  // Open Edit Batch
  // --------------------------------------------------

  const openEditBatch = (
    batch
  ) => {
    setEditBatchName(
      batch.name || ''
    );


    setEditBatchTotal(
      String(
        batch.total_spent ??
          ''
      )
    );


    setShowEditModal(
      true
    );
  };


  // --------------------------------------------------
  // Save Batch Changes
  // --------------------------------------------------

  const saveBatchChanges =
    async () => {
      if (!selectedBatch) {
        return;
      }


      const name =
        editBatchName.trim();


      const totalSpent =
        Number(
          editBatchTotal
        );


      if (!name) {
        alert(
          'Enter a batch name.'
        );

        return;
      }


      if (
        !Number.isFinite(
          totalSpent
        ) ||
        totalSpent < 0
      ) {
        alert(
          'Enter a valid total spent.'
        );

        return;
      }


      try {
        setSavingEdit(
          true
        );


        const response =
          await api.put(
            `/purchase-batches/${selectedBatch.id}`,
            {
              name,

              total_spent:
                totalSpent,
            }
          );


        console.log(
          '[PurchaseBatches] Update response:',
          response.data
        );


        setShowEditModal(
          false
        );


        await fetchBatches();


        await openBatch(
          selectedBatch.id
        );
      } catch (err) {
        console.error(
          '[PurchaseBatches] Update error:',
          err
        );


        alert(
          err.response?.data?.error ||
            err.response?.data
              ?.details ||
            'Failed to update purchase batch.'
        );
      } finally {
        setSavingEdit(
          false
        );
      }
    };


  // --------------------------------------------------
  // Manual Recalculate
  // --------------------------------------------------

  const recalculateBatch =
    async () => {
      if (!selectedBatch) {
        return;
      }


      try {
        setRecalculating(
          true
        );


        const response =
          await api.post(
            `/purchase-batches/${selectedBatch.id}/recalculate`
          );


        console.log(
          '[PurchaseBatches] Recalculate response:',
          response.data
        );


        await fetchBatches();


        await openBatch(
          selectedBatch.id
        );
      } catch (err) {
        console.error(
          '[PurchaseBatches] Recalculate error:',
          err
        );


        alert(
          err.response?.data?.error ||
            err.response?.data
              ?.details ||
            'Failed to recalculate purchase batch.'
        );
      } finally {
        setRecalculating(
          false
        );
      }
    };


  // --------------------------------------------------
  // Remove SKU from Purchase Batch
  //
  // This does NOT delete the draft or product.
  //
  // It ONLY removes the SKU from this Purchase Batch.
  //
  // Backend will:
  // - remove purchase_batch_items row
  // - recalculate remaining SKUs
  // - clean up the removed SKU's old batch allocation
  // --------------------------------------------------

  const removeSkuFromBatch =
    async (item) => {
      if (
        !selectedBatch ||
        !item?.sku
      ) {
        return;
      }


      const confirmed =
        window.confirm(
          `Remove "${item.sku}" from "${selectedBatch.name}"?\n\n` +
            `This will NOT delete the draft or product.\n\n` +
            `It will only remove this SKU from the Purchase Batch and recalculate the remaining items.`
        );


      if (!confirmed) {
        return;
      }


      try {
        setRemovingSku(
          item.sku
        );


        const response =
          await api.delete(
            `/purchase-batches/item/${encodeURIComponent(
              item.sku
            )}`
          );


        console.log(
          '[PurchaseBatches] Remove SKU response:',
          response.data
        );


        await fetchBatches();


        await openBatch(
          selectedBatch.id
        );
      } catch (err) {
        console.error(
          '[PurchaseBatches] Remove SKU error:',
          err
        );


        alert(
          err.response?.data?.error ||
            err.response?.data
              ?.details ||
            'Failed to remove SKU from purchase batch.'
        );
      } finally {
        setRemovingSku('');
      }
    };


  // --------------------------------------------------
  // Money helper
  // --------------------------------------------------

  const money = (
    value
  ) => {
    const number =
      Number(value);


    if (
      !Number.isFinite(
        number
      )
    ) {
      return '$0.00';
    }


    return `$${number.toFixed(
      2
    )}`;
  };


  return (
    <Container className="py-4">

      <div className="d-flex justify-content-between align-items-center mb-4">

        <div>

          <h2 className="mb-1">
            Purchase Batches
          </h2>

          <div className="text-muted">
            Track garage sales,
            estate sales,
            collections, bulk buys,
            and other grouped
            purchases.
          </div>

        </div>


        <Button
          variant="primary"
          onClick={() =>
            setShowCreateModal(
              true
            )
          }
        >
          + Create Batch
        </Button>

      </div>


      <Alert variant="info">

        <strong>
          Automatic Cost Allocation:
        </strong>{' '}

        Costs are automatically
        redistributed whenever a SKU
        is added, removed, moved to
        another batch, or the total
        spent is changed.

      </Alert>


      {error && (

        <Alert variant="danger">
          {error}
        </Alert>

      )}


      {loading ? (

        <div className="text-center py-5">

          <Spinner
            animation="border"
          />

          <div className="mt-2">
            Loading purchase batches...
          </div>

        </div>

      ) : batches.length ===
        0 ? (

        <Card>

          <Card.Body className="text-center py-5">

            <h5>
              No Purchase Batches Yet
            </h5>

            <p className="text-muted mb-3">
              Create your first batch
              to start grouping SKUs
              and tracking their
              purchase cost.
            </p>

            <Button
              onClick={() =>
                setShowCreateModal(
                  true
                )
              }
            >
              Create First Batch
            </Button>

          </Card.Body>

        </Card>

      ) : (

        <div className="row g-3">

          {batches.map(
            (batch) => {

              const itemCount =
                Number(
                  batch.item_count
                ) || 0;


              const totalSpent =
                Number(
                  batch.total_spent
                ) || 0;


              const currentCost =
                itemCount > 0
                  ? totalSpent /
                    itemCount
                  : 0;


              return (

                <div
                  className="col-12 col-md-6 col-xl-4"
                  key={
                    batch.id
                  }
                >

                  <Card className="h-100 shadow-sm">

                    <Card.Body>


                      <div className="d-flex justify-content-between align-items-start">

                        <h5 className="mb-2">
                          {
                            batch.name
                          }
                        </h5>


                        <Badge bg="success">
                          ACTIVE
                        </Badge>

                      </div>


                      <div className="mb-2">

                        <strong>
                          Total Spent:
                        </strong>{' '}

                        {money(
                          batch.total_spent
                        )}

                      </div>


                      <div className="mb-2">

                        <strong>
                          Items:
                        </strong>{' '}

                        {
                          itemCount
                        }

                      </div>


                      <div className="mb-3">

                        <strong>
                          Current Cost / Item:
                        </strong>{' '}

                        {money(
                          currentCost
                        )}

                      </div>


                      <div className="text-muted small mb-3">
                        Automatically
                        recalculates as
                        items are added
                        or removed.
                      </div>


                      <Button
                        variant="outline-primary"
                        onClick={() =>
                          openBatch(
                            batch.id
                          )
                        }
                      >
                        View Batch
                      </Button>


                    </Card.Body>

                  </Card>

                </div>

              );
            }
          )}

        </div>

      )}


      {/* --------------------------------------------------
          CREATE BATCH MODAL
      -------------------------------------------------- */}

      <Modal
        show={
          showCreateModal
        }
        onHide={() => {

          if (
            !creating
          ) {
            setShowCreateModal(
              false
            );
          }

        }}
        centered
      >

        <Modal.Header
          closeButton
        >

          <Modal.Title>
            Create Purchase Batch
          </Modal.Title>

        </Modal.Header>


        <Modal.Body>


          <Form.Group className="mb-3">

            <Form.Label>
              Batch Name
            </Form.Label>

            <Form.Control
              type="text"
              value={
                newBatchName
              }
              onChange={(
                event
              ) =>
                setNewBatchName(
                  event.target
                    .value
                )
              }
              placeholder="e.g. Garage Sale 2"
              autoFocus
            />

          </Form.Group>


          <Form.Group>

            <Form.Label>
              Total Spent ($)
            </Form.Label>

            <Form.Control
              type="number"
              value={
                newBatchTotal
              }
              onChange={(
                event
              ) =>
                setNewBatchTotal(
                  event.target
                    .value
                )
              }
              placeholder="0.00"
              min="0"
              step="0.01"
            />

          </Form.Group>


          <Form.Text className="text-muted d-block mt-3">

            The total spent will be
            evenly distributed across
            every SKU assigned to this
            batch.

          </Form.Text>


        </Modal.Body>


        <Modal.Footer>


          <Button
            variant="secondary"
            disabled={
              creating
            }
            onClick={() =>
              setShowCreateModal(
                false
              )
            }
          >
            Cancel
          </Button>


          <Button
            variant="primary"
            disabled={
              creating
            }
            onClick={
              createBatch
            }
          >
            {creating
              ? 'Creating...'
              : 'Create Batch'}
          </Button>


        </Modal.Footer>

      </Modal>


      {/* --------------------------------------------------
          VIEW BATCH MODAL
      -------------------------------------------------- */}

      <Modal
        show={
          selectedBatch !==
            null ||
          loadingBatch
        }
        onHide={() => {

          if (
            !recalculating &&
            !removingSku
          ) {
            setSelectedBatch(
              null
            );
          }

        }}
        size="lg"
        centered
      >

        <Modal.Header
          closeButton
        >

          <Modal.Title>
            {selectedBatch?.name ||
              'Purchase Batch'}
          </Modal.Title>

        </Modal.Header>


        <Modal.Body>


          {loadingBatch &&
          !selectedBatch ? (

            <div className="text-center py-4">

              <Spinner
                animation="border"
              />

            </div>

          ) : selectedBatch ? (

            <>


              <Alert variant="success">

                <strong>
                  Active Batch
                </strong>

                <div className="mt-1">
                  Costs automatically
                  rebalance whenever
                  items are added,
                  removed, or the total
                  spent changes.
                </div>

              </Alert>


              <div className="row mb-4">


                <div className="col-md-3 mb-2">

                  <div className="text-muted small">
                    Total Spent
                  </div>

                  <strong>
                    {money(
                      selectedBatch
                        .total_spent
                    )}
                  </strong>

                </div>


                <div className="col-md-3 mb-2">

                  <div className="text-muted small">
                    Items
                  </div>

                  <strong>
                    {
                      selectedBatch
                        .item_count
                    }
                  </strong>

                </div>


                <div className="col-md-3 mb-2">

                  <div className="text-muted small">
                    Current Cost / Item
                  </div>

                  <strong>
                    {money(
                      selectedBatch
                        .estimated_cost_per_item
                    )}
                  </strong>

                </div>


                <div className="col-md-3 mb-2">

                  <div className="text-muted small">
                    Status
                  </div>

                  <Badge bg="success">
                    ACTIVE
                  </Badge>

                </div>


              </div>


              <div className="d-flex flex-wrap gap-2 mb-4">

                <Button
                  variant="outline-primary"
                  onClick={() =>
                    openEditBatch(
                      selectedBatch
                    )
                  }
                >
                  Edit Batch
                </Button>


                <Button
                  variant="outline-secondary"
                  disabled={
                    recalculating ||
                    !!removingSku
                  }
                  onClick={
                    recalculateBatch
                  }
                >
                  {recalculating
                    ? 'Recalculating...'
                    : 'Recalculate Costs'}
                </Button>

              </div>


              <h6>
                SKUs in this Batch
              </h6>


              {selectedBatch
                .items?.length >
              0 ? (

                <div
                  style={{
                    maxHeight:
                      '400px',
                    overflowY:
                      'auto',
                  }}
                >

                  <Table
                    striped
                    bordered
                    hover
                    responsive
                  >

                    <thead>

                      <tr>

                        <th>
                          SKU
                        </th>

                        <th>
                          Allocated Cost
                        </th>

                        <th>
                          Added
                        </th>

                        <th
                          style={{
                            width:
                              '120px',
                          }}
                        >
                          Action
                        </th>

                      </tr>

                    </thead>


                    <tbody>


                      {selectedBatch.items.map(
                        (
                          item
                        ) => {

                          const isRemoving =
                            removingSku ===
                            item.sku;


                          return (

                            <tr
                              key={
                                item.id
                              }
                            >

                              <td>
                                {
                                  item.sku
                                }
                              </td>


                              <td>

                                {item.allocated_cost ===
                                null
                                  ? 'Pending'
                                  : money(
                                      item.allocated_cost
                                    )}

                              </td>


                              <td>

                                {item.created_at
                                  ? new Date(
                                      item.created_at
                                    ).toLocaleString()
                                  : ''}

                              </td>


                              <td>

                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  disabled={
                                    !!removingSku ||
                                    recalculating
                                  }
                                  onClick={() =>
                                    removeSkuFromBatch(
                                      item
                                    )
                                  }
                                >
                                  {isRemoving
                                    ? 'Removing...'
                                    : 'Remove'}
                                </Button>

                              </td>


                            </tr>

                          );
                        }
                      )}


                    </tbody>

                  </Table>

                </div>

              ) : (

                <Alert variant="secondary">

                  No SKUs have been
                  assigned to this
                  batch yet.

                </Alert>

              )}


            </>

          ) : null}


        </Modal.Body>


        <Modal.Footer>


          <Button
            variant="secondary"
            disabled={
              recalculating ||
              !!removingSku
            }
            onClick={() =>
              setSelectedBatch(
                null
              )
            }
          >
            Close
          </Button>


        </Modal.Footer>

      </Modal>


      {/* --------------------------------------------------
          EDIT BATCH MODAL
      -------------------------------------------------- */}

      <Modal
        show={
          showEditModal
        }
        onHide={() => {

          if (
            !savingEdit
          ) {
            setShowEditModal(
              false
            );
          }

        }}
        centered
      >

        <Modal.Header
          closeButton
        >

          <Modal.Title>
            Edit Purchase Batch
          </Modal.Title>

        </Modal.Header>


        <Modal.Body>


          <Form.Group className="mb-3">

            <Form.Label>
              Batch Name
            </Form.Label>

            <Form.Control
              type="text"
              value={
                editBatchName
              }
              onChange={(
                event
              ) =>
                setEditBatchName(
                  event.target
                    .value
                )
              }
            />

          </Form.Group>


          <Form.Group>

            <Form.Label>
              Total Spent ($)
            </Form.Label>

            <Form.Control
              type="number"
              value={
                editBatchTotal
              }
              onChange={(
                event
              ) =>
                setEditBatchTotal(
                  event.target
                    .value
                )
              }
              min="0"
              step="0.01"
            />

          </Form.Group>


          <Alert
            variant="warning"
            className="mt-3 mb-0"
          >

            Changing the total spent
            will immediately
            redistribute the cost
            across every SKU in this
            batch.

          </Alert>


        </Modal.Body>


        <Modal.Footer>


          <Button
            variant="secondary"
            disabled={
              savingEdit
            }
            onClick={() =>
              setShowEditModal(
                false
              )
            }
          >
            Cancel
          </Button>


          <Button
            variant="primary"
            disabled={
              savingEdit
            }
            onClick={
              saveBatchChanges
            }
          >
            {savingEdit
              ? 'Saving...'
              : 'Save Changes'}
          </Button>


        </Modal.Footer>

      </Modal>


    </Container>
  );
}


export default PurchaseBatches;